import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import type { ProjectWithSites, WarrantyContact, AssetManagerInvite } from '../models/Project';

import { EmailStatus } from '../models/Project';
import commissioningApiClient from '@/http/commissioningApiClient';
import api, { comApi } from '@/http/api';
import { resolveHttpErrorMessage } from '@/http/http-error';
import { validateIsEmail } from '@/utils/validators';
import { ProjectFormModel, SiteFormModel } from '../models/Project';
import { setLocaleByCountryCode } from '@/i18n';
import type { COUNTRY_CODE } from '@/utils/constants';
import { COUNTRY_CODE as CountryCode } from '@/utils/constants';

/**
 * Purpose of useProjectStore:
 * - Stores all projects info that the current logged in commisisoner have access to, not just the site being commissioned.
 * - Each project includes its sites info.
 */
export const useProjectStore = defineStore('project', () => {
  /** List of projects in commissioning state this commissioner has access to */
  const commissioningProjects = ref<ProjectWithSites[]>([]);

  /**
   * List of all projects this commissioner has access to.
   * This includes projects that are not in commissioning state.
   */
  const allProjects = ref<ProjectWithSites[]>([]);

  /**
   * Reference to the new project being created.
   * Note to set the ID to 0 to indicate a new project.
   */
  const newProject = ref<ProjectFormModel>(new ProjectFormModel());

  /**
   * A computed property that tells whether a new project can be created.
   * This is to make sure no duplicate projects are created when the user navigates
   * back to the project create form.
   * Note: Call the resetNewProject() to allow the user create a new project.
   */
  const allowedToCreateNewProject = computed(() => {
    return newProject.value.id === 0;
  });

  /**
   * Reference to the warranty contact being created.
   */
  const warrantyContact = ref<WarrantyContact>({
    company: '',
    firstName: '',
    lastName: '',
    contactEmail: '',
    contactPhone: '',
  });

  /**
   * List of asset manager invites for the current project.
   */
  const assetManagerInvites = ref<AssetManagerInvite[]>([]);

  /** Country code detected from the user's IP address (ISO 3166-1 alpha-2). */
  const userCountryCodeDetected = ref<string | null>(null);
  const userCountryDetectionAttempted = ref(false);

  const isUserInUK = computed(
    () => userCountryCodeDetected.value === CountryCode['United Kingdom'],
  );

  const normalizeDetectedCountryCode = (code: string) => code.trim().toUpperCase();

  /**
   * Detect the user's country from their IP address.
   * Used to choose address lookup providers (e.g. Ideal Postcodes for UK users).
   */
  const fetchUserCountryCode = async () => {
    if (userCountryDetectionAttempted.value) {
      return;
    }
    userCountryDetectionAttempted.value = true;

    if ('Cypress' in window) {
      return;
    }

    let countryCode: string = CountryCode.Australia;
    try {
      const { data } = await axios.get<{ country: string }>(api.getUserLocation1);
      countryCode = data.country;
    } catch {
      try {
        const { data } = await axios.get<{ country_code: string }>(api.getUserLocation2);
        countryCode = data.country_code;
      } catch {
        console.error('get location from ip failed');
      }
    }
    userCountryCodeDetected.value = normalizeDetectedCountryCode(countryCode);
  };

  const fetchCommissioningProjects = async () => {
    const LIMIT = 5;
    const query = new URLSearchParams({
      limit: LIMIT.toString(),
      type: 'commissioning',
    });
    const getProjectsEndpoint = `${api.projects}?${query.toString()}`;

    try {
      const { data } = await commissioningApiClient.get(getProjectsEndpoint);
      commissioningProjects.value = data.data;
    } catch (error: unknown) {
      const errorMessage = resolveHttpErrorMessage(
        error,
        `Failed to fetch projects with ${getProjectsEndpoint}`,
      );
      throw new Error(errorMessage);
    }
  };

  const fetchAllProjects = async () => {
    const LIMIT = 100;
    const query = new URLSearchParams({
      limit: LIMIT.toString(),
    });
    const getProjectsEndpoint = `${api.projects}?${query.toString()}`;

    try {
      const { data } = await commissioningApiClient.get(getProjectsEndpoint);
      allProjects.value = data.data;
    } catch (error: unknown) {
      const errorMessage = resolveHttpErrorMessage(
        error,
        `Failed to fetch all projects with ${getProjectsEndpoint}`,
      );
      throw new Error(errorMessage);
    }
  };

  /**
   * Get the project with the given id.
   * Source is either the commissioningProjects list or the allProjects list.
   * @param projectId - The project id to get.
   * @returns The project with the given id or undefined if not found.
   */
  const getProjectById = (projectId: number) => {
    const commissionedProject = commissioningProjects.value.find(
      (project) => project.id === projectId,
    );
    return commissionedProject ?? allProjects.value.find((project) => project.id === projectId);
  };

  /**
   * Retrieve site details for the given project id and site id.
   */
  const siteDetails = (projectId: number, siteId: number) => {
    const project = getProjectById(projectId);
    if (!project) return null;

    const site = project.sites.find((site) => site.id === siteId);
    if (!site) return null;

    return {
      projectId: project.id,
      projectName: project.name,
      address: site.address,
    };
  };

  /**
   * This function will open up the project create form
   */
  const resetNewProject = () => {
    newProject.value = new ProjectFormModel();
  };

  /**
   * Save the project to the backend database and then to the store.
   * @param uiProjectData - The project data from the UI (which includes the long, lat, and site country).
   */
  const createProject = async (uiProjectData: ProjectFormModel) => {
    try {
      // Save the project to the backend database
      const createProjectEndpoint = api.projects;
      const { data: dbProjectData } = await commissioningApiClient.post(
        createProjectEndpoint,
        uiProjectData.serialize(),
      );

      // OnSuccess, save the response data to the store's newProject
      newProject.value = new ProjectFormModel().deserialize(dbProjectData);

      // Then add the project to the commissioningProjects list
      __addOrUpdateProjectForCommissioning(dbProjectData);

      // Update the locale to the project country manually since the locale watcher (based on solshareSite.country) won't be triggered yet.
      setLocaleByCountryCode(dbProjectData.country as COUNTRY_CODE);
    } catch (error: unknown) {
      __handleProjectError(error, 'Failed to save new project');
    }
  };

  /**
   * Update the project in the backend database and then update the store.
   * @param projectId - The project id to update.
   * @param uiProjectData - The project data from the UI (which includes the long, lat, and site country).
   */
  const updateProject = async (projectId: number, uiProjectData: ProjectFormModel) => {
    try {
      const updateProjectEndpoint = `${api.projects}/${projectId}`;
      const { data: dbProjectData } = await commissioningApiClient.patch(
        updateProjectEndpoint,
        uiProjectData.serialize(),
      );

      // Then add/update the project to the commissioningProjects list
      __addOrUpdateProjectForCommissioning(dbProjectData);
    } catch (error: unknown) {
      __handleProjectError(error, 'Failed to update project');
    }
  };

  /**
   * Add the sites to an existing project in the backend database and then update the store.
   * @param projectId - The project id to add the sites to.
   * @param sites - The sites to add to the project.
   */
  const addSitesToProject = async (projectId: number, sites: SiteFormModel[]) => {
    if (!sites || sites.length === 0) throw new Error('No sites to add to project');

    try {
      const addSitesEndpoint = comApi.addSitesToProject(projectId);
      const { data: dbSitesData } = await commissioningApiClient.post(addSitesEndpoint, {
        sites: sites.map((site) => site.serialize()),
      });

      // Then add/update the project to the commissioningProjects list
      __addOrUpdateProjectForCommissioning(dbSitesData);
    } catch (error: unknown) {
      __handleProjectError(error, 'Failed to add sites to project');
    }
  };

  /**
   * Save the warranty contact to the backend database and then to the store.
   * The current behavior is that after keying the warranty contact,
   *  it will be persisted to the backend right away.
   * @param projectId - The project ID to save the warranty contact to.
   * @param contact - The warranty contact to save.
   */
  const saveWarrantyContact = async (projectId: number, contact: WarrantyContact) => {
    if (!contact) throw new Error('Warranty contact is required');

    try {
      // Persist to the backend database first
      await commissioningApiClient.post(comApi.addWarrantyContact(projectId), {
        company: contact.company,
        firstName: contact.firstName,
        lastName: contact.lastName,
        contactEmail: contact.contactEmail,
        contactPhone: contact.contactPhone,
      });

      // then save to the store on success
      __setWarrantyContact(contact);
    } catch (error: unknown) {
      const errorMessage = resolveHttpErrorMessage(error, 'Failed to upload warranty contact');
      throw new Error(errorMessage);
    }
  };

  /**
   * Save the asset manager invite to the backend database and then to the store.
   * @param serial - The serial number of the device.
   * @param invite - The asset manager invite.
   */
  const sendAssetManagerInvite = async (serial: string, invite: AssetManagerInvite) => {
    if (!serial) throw new Error('Serial number is required');
    if (!invite || !__trimmedEmailValidityCheck(invite.email))
      throw new Error('Asset manager invite is required');

    try {
      invite.status = EmailStatus.Sending;
      await commissioningApiClient.post(comApi.inviteAssetManager(serial), {
        email: invite.email,
        serialNum: serial,
      });
      invite.status = EmailStatus.Sent;
    } catch (error: unknown) {
      invite.status = EmailStatus.Failed;
      const errorMessage = resolveHttpErrorMessage(error, 'Failed to invite asset manager');

      if (errorMessage.toLowerCase().includes('has already linked with project')) {
        throw new Error('This email is already linked with project.');
      } else {
        throw new Error('Failed to send invite, please try again.');
      }
    } finally {
      __addOrUpdateAssetManagerInvite(invite);
    }
  };

  /**
   * Store the asset manager invites in the store.
   * Mass UI update of the invites from the form inputs.
   *
   * This function ensures that:
   * 1/ Empty or invalid emails are not saved
   * 2/ Save the email invite regardless of the status
   *    - Unsent invites are still saved in case the user wants to send them later
   * 3/ Does not overwrite an invite if it has already been sent previously
   * @param invites - The asset manager invites to store.
   */
  const storeAssetManagerInvites = async (invites: AssetManagerInvite[]) => {
    assetManagerInvites.value = [];

    for (const invite of invites) {
      if (__trimmedEmailValidityCheck(invite.email)) {
        __addOrUpdateAssetManagerInvite(invite);
      }
    }
  };

  /**
   * A private function to check if the email is valid and not empty.
   * @param email - The email to check.
   * @returns True if the email is valid and not empty, false otherwise.
   */
  const __trimmedEmailValidityCheck = (email: string) =>
    email && email.trim() !== '' && validateIsEmail(email);

  /**
   * This private function can be called to update the project for commissioning
   *  after other operations has created a new project or updated a project in the backend.
   * Otherwise, just fetch all projects from the backend to get the latest data.
   * @param dbProjectData - The project data from the backend.
   */
  const __addOrUpdateProjectForCommissioning = async (dbProjectData: ProjectWithSites) => {
    const index = commissioningProjects.value.findIndex(
      (project) => project.id === dbProjectData.id,
    );
    if (index !== -1) {
      commissioningProjects.value[index] = { ...dbProjectData };
    } else {
      commissioningProjects.value.push({
        ...dbProjectData,
        sites: dbProjectData.sites.map((site) => ({
          ...site,
          commissionedSolshareCount: site.commissionedSolshareCount || 0,
        })),
      });
    }
  };

  /**
   * Save the warranty contact just to the store.
   * @param contact - The warranty contact to set.
   */
  const __setWarrantyContact = (contact: WarrantyContact) => {
    warrantyContact.value = { ...contact };
  };

  /**
   * A private function that add or update an asset manager invite in the store.
   * It uses a non-case sensitive and trimmed email to compare invites.
   *
   * This function ensures that:
   * 1/ Add the invite to the store if it is not already in the store
   * 2/ Does not replace the invite info if it has already been sent before
   * @param invite - The asset manager invite to add or update.
   */
  const __addOrUpdateAssetManagerInvite = (invite: AssetManagerInvite) => {
    const __lowerTrimmedEmail = (email: string) => email?.toLowerCase().trim();

    const inviteToAddOrUpdate = { ...invite };
    const foundIndex = assetManagerInvites.value.findIndex(
      (i) => __lowerTrimmedEmail(i.email) === __lowerTrimmedEmail(inviteToAddOrUpdate.email),
    );

    if (foundIndex !== -1) {
      /**
       * Do not replace an invite if it has already been sent previously.
       *
       * This is an edge case scenario for this check:
       *  - The user previously sent the invite and then added in the same email again without sending.
       */
      if (assetManagerInvites.value[foundIndex].status !== EmailStatus.Sent) {
        assetManagerInvites.value[foundIndex] = inviteToAddOrUpdate;
      }
    } else {
      // Add the invite to the store if it is not already in the store
      assetManagerInvites.value.push(inviteToAddOrUpdate);
    }
  };

  /**
   * Handle the error from the create, add sites to project operation.
   * @param error - The error to handle.
   * @param genericErrorMessage - The generic error message to throw.
   * @returns The error message to throw.
   */
  const __handleProjectError = (error: unknown, genericErrorMessage: string) => {
    const errorMessage = resolveHttpErrorMessage(error, genericErrorMessage);

    if (!Array.isArray(errorMessage)) {
      throw new Error(genericErrorMessage);
    }

    const __matchesAny = (needles: string[]) =>
      errorMessage.some((error) =>
        needles.some((needle) => error.toLowerCase().includes(needle.toLowerCase())),
      );

    if (__matchesAny(['country must be a valid country code']))
      throw new Error(
        'The site address must be in Australia, the UK, the US, Germany, or New Zealand.',
      );

    if (
      __matchesAny([
        'address must be a string',
        'longitude must be a number',
        'latitude must be a number',
      ])
    )
      throw new Error('Please enter a valid site address');

    if (
      __matchesAny([
        'plannedsolsharecount must be a number',
        'plannedsolsharecount must not be less than 1',
      ])
    )
      throw new Error('Please enter a valid number of SolShares');

    throw new Error(genericErrorMessage);
  };

  return {
    // Properties
    commissioningProjects,
    allProjects,
    newProject,
    allowedToCreateNewProject,
    warrantyContact,
    assetManagerInvites,
    userCountryCodeDetected,
    isUserInUK,

    // Actions
    fetchCommissioningProjects,
    fetchAllProjects,
    fetchUserCountryCode,
    getProjectById,
    siteDetails,
    resetNewProject,
    createProject,
    addSitesToProject,
    updateProject,
    saveWarrantyContact,
    sendAssetManagerInvite,
    storeAssetManagerInvites,
  };
});
